import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import "./settings.css";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { errorLog, infoLog } from "../../utils/logger";
import { DateTime } from "luxon";
import {
  checkBoxValueMap,
  getSearchRadios,
  getLocalizedLegalSexChoices,
} from "../../utils/formUtils";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import lodash, { set } from "lodash";
import { formatNumber, getDateTime } from "../../utils/data";
import EnterPhonePin from "../onboarding/EnterPhonePin";
import VerifyPhone from "../onboarding/VerifyPhone";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import { DocketSwitch } from "../../components/form/DocketSwitch";
import { getAuth } from "firebase/auth";
import logout from "../../utils/logout";
import { useAtom } from "jotai";
import {
  tokensAtom,
  userAtom,
  highContrastAtom,
  consentAcceptedAtomFamily,
} from "../../jotai/atoms";
import MobileMenu from "./MobileMenu";

export function Settings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname, state } = useLocation();

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalMessage, setStatusModalMessage] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [highContrast, setHighContrast] = useAtom(highContrastAtom);
  const [deleteAccountChecked, setDeleteAccountChecked] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | boolean>(false);

  const [dob, setDob] = useState<Date | null>(null);

  // DOB checkbox, to map selection to values
  // ToDo: set initial state from prev search or user object
  const [checkboxValue, setCheckboxValue] = useState(getLocalizedLegalSexChoices(t));
  const checkboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(checkboxValue, index);
    infoLog(`newValue ${JSON.stringify(newValue)}`);
    setCheckboxValue(newValue);
  };
  const [selectedDisableAll, setSelectedDisableAll] = useState<boolean>(false);
  const [selectedEmailReminders, setSelectedEmailReminders] = useState<boolean>(false);
  const [selectedEmailRecordUpdates, setSelectedEmailRecordUpdates] = useState<boolean>(false);
  const [selectedAppUpdates, setSelectedAppUpdates] = useState<boolean>(false);

  const [verifyStep, setVerifyStep] = useState(true);
  const [user, setUser] = useAtom(userAtom);
  const [consentAccepted, setConsentAccepted] = useAtom(
    consentAcceptedAtomFamily(user?.email ?? "")
  );

  const searchFormTranslations = {
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
  };

  const updateName = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
  });

  const updateProfile = yup.object().shape({
    dateOfBirth: yup.string().required(),
    legalSex: yup.string().required(),
  });

  const {
    register,
    formState: { errors, isValid, isDirty, isSubmitting },
    handleSubmit,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(updateName),
  });

  const {
    register: register3,
    formState: { errors: errors3, isValid: isValid3 },
    handleSubmit: handleSubmitUserInfo,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(updateProfile),
  });
  const { register: register4, handleSubmit: handleDeleteAccount } = useForm();

  const [tokens, setTokens] = useAtom(tokensAtom);

  useEffect(() => {
    setSelectedDisableAll(user?.notification_preferences?.disable_all || false);
    setSelectedEmailRecordUpdates(user?.notification_preferences?.email_new_results || false);
    setSelectedEmailReminders(user?.notification_preferences?.email_upcoming_vaccines || false);
    setSelectedAppUpdates(user?.notification_preferences?.email_app_updates || false);

    const newValue = checkBoxValueMap(
      checkboxValue,
      lodash.findIndex(checkboxValue, function (item) {
        return (
          item.value === user?.legal_sex || item.value === user?.legal_sex?.charAt(0).toUpperCase()
        );
      })
    );
    setCheckboxValue(newValue);
    if (user?.dob) {
      setDob(getDateTime(user.dob).toJSDate());
    }
    return () => {};
  }, [user]);

  const getStatusModal = () => {
    return (
      <>
        <Modal
          id="status-modal"
          open={showStatusModal}
          onConfirm={() => navFromModal()}
          title={t("menu.settings")}
          onCloseModal={() => setShowStatusModal(false)}
          hasClose={true}
        >
          <p>{statusModalMessage}</p>
        </Modal>
      </>
    );
  };

  const getDeleteAccountModal = () => {
    return (
      <Modal
        id="delete-account-modal"
        open={showDeleteModal}
        hasClose={true}
        title={t("settings.delete_account")}
        onCloseModal={() => setShowDeleteModal(false)}
        actionButtons={
          <>
            <button
              className="btn docket-button secondary"
              aria-label={t("generic.cancel")}
              onClick={() => setShowDeleteModal(false)}
            >
              {t("generic.cancel")}
            </button>
            <button
              disabled={deletingAccount}
              className="btn docket-button"
              aria-label={t("generic.delete")}
              onClick={() => onDeleteUserAccount()}
            >
              {t("generic.delete")}
            </button>
          </>
        }
      >
        <p>{t("settings.delete_account_message")}</p>
      </Modal>
    );
  };

  const navFromModal = () => {
    setShowStatusModal(false);
    setStatusModalMessage("");
  };

  const onSubmit = async (data: any) => {
    try {
      let params = {
        first_name: data.firstName,
        last_name: data.lastName,
      };
      infoLog(`name update params ${JSON.stringify(params)}`);
      await getAPIClient().updateUser(params);
      let userResults = await getAPIClient().getUser();
      if (userResults) {
        // we still need to persist our tokens
        await setUser(userResults);
      }
      setShowStatusModal(true);
      setStatusModalMessage("Name updated successfully.");
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }
  };

  const getInputField = (
    yupField: "firstName" | "lastName",
    displayName: string,
    defaultVal: string = ""
  ) => {
    return (
      <div key={yupField}>
        <fieldset className="fieldset p-0">
          <legend className="fieldset-legend pt-0 text-[16px] font-[700] text-black dark:text-white mobile-text-color">
            {displayName} <span className="required-tag"> ({t("system.required")})</span>
          </legend>
          <input
            data-testid={`input-field-${yupField}`}
            className="input max-md:w-full input-responsive-white text-black dark:text-white"
            type="text"
            placeholder={displayName}
            defaultValue={defaultVal}
            {...register(yupField)}
          />
        </fieldset>
      </div>
    );
  };

  const formTranslations = {
    email: t("onboarding.email-address"),
    firstName: t("immunizations.search_my_first_name_label"),
    lastName: t("immunizations.search_my_last_name_label"),
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
    childFirstName: t("immunizations.search_child_first_name_label"),
    childLastName: t("immunizations.search_child_last_name_label"),
    childDateOfBirth: t("immunizations.search_child_dob_label"),
    childLegalSex: t("immunizations.search_child_legal_sex_label"),
    consentTitle: t("immunizations.search_consent_title"),
    consentMessage: t("immunizations.search_consent_message"),
    consentCheckBoxLabel: t("immunizations.i_consent"),
  };

  const ageCheckPassed = () => {
    if (dob && DateTime.now().minus({ years: 13 }) <= DateTime.fromJSDate(dob)) {
      return false;
    }
    return true;
  };

  const onSubmitUserInfo = async () => {
    if (!ageCheckPassed()) {
      errorLog({ message: t("settings.dob_error_message") } as Error);
      setShowErrorMessage(`${t("settings.dob_error_message")}`);
      return;
    }
    try {
      let sex = lodash.find(checkboxValue, ["checked", true]);
      let legalSex = sex?.value ?? "U";
      const params = {
        legal_sex: legalSex,
        dob: dob ? DateTime.fromJSDate(dob).toISO() : null,
        language: "en",
      };
      infoLog(`onSubmitUserInfo ${JSON.stringify(params)}`);
      await getAPIClient().updateUser(params);
      let userResults = await getAPIClient().getUser();
      if (userResults) {
        await setUser({
          ...userResults,
          dob: dob ? DateTime.fromJSDate(dob).toLocaleString() : null,
        });
      }
      setShowStatusModal(true);
      setStatusModalMessage("Profile updated successfully.");
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }
  };

  const getUserUpdateForm = () => {
    return (
      <div className="mt-2 mobile-text-color">
        <div>
          <label
            className="text-dark dark:text-white mobile-text-color font-bold"
            htmlFor="dateOfBirth"
          >
            {searchFormTranslations.dateOfBirth}
          </label>
        </div>
        <ResponsiveDatePicker
          key="dateOfBirth"
          selectedDate={dob}
          setSelectedDate={(d) => setDob(d)}
        />
        <br />
        {getSearchRadios(
          checkboxValue,
          searchFormTranslations.legalSex,
          checkboxHandler,
          "",
          "gender",
          t
        )}
        <br />
      </div>
    );
  };

  const getDeleteAccountForm = () => {
    return (
      <label className="text-black dark:text-white mobile-text-color">
        <input
          type="checkbox"
          checked={deleteAccountChecked}
          onChange={() => setDeleteAccountChecked(!deleteAccountChecked)}
          className="checkbox md:!border-gray-500"
        />
        &nbsp;&nbsp; {t("settings.delete_account_confirmation")}
      </label>
    );
  };

  const onDeleteUserAccount = async () => {
    try {
      // Disable the button
      setDeletingAccount(true);
      // Initiates the deleteToken() operation and continues without waiting.
      await getAPIClient().deleteUser();
      setConsentAccepted(false);
      await logout(getAPIClient(), getAuth());
      navigate("/");
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
      if (e instanceof Error) {
        errorLog(e);
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const notificationHasChanged = () => {
    return (
      selectedDisableAll !== user?.notification_preferences?.disable_all ||
      selectedEmailRecordUpdates !== user?.notification_preferences?.email_new_results ||
      selectedEmailReminders !== user?.notification_preferences?.email_upcoming_vaccines ||
      selectedAppUpdates !== user?.notification_preferences?.email_app_updates
    );
  };

  const handleSaveNotificationPreferences = async () => {
    const notification_preferences = {
      disable_all: selectedDisableAll,
      email_new_results: selectedDisableAll ? false : selectedEmailRecordUpdates,
      email_upcoming_vaccines: selectedDisableAll ? false : selectedEmailReminders,
      email_app_updates: selectedDisableAll ? false : selectedAppUpdates,
    };
    try {
      await getAPIClient().updateUser({ notification_preferences });
      let userResults = await getAPIClient().getUser();
      if (userResults) {
        await setUser(userResults);
      }
      setShowStatusModal(true);
      setStatusModalMessage("Notification preferences updated successfully.");
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }
  };

  return (
    <>
      <div className="md:hidden">
        <MobileMenu />
      </div>
      <div className="max-md:hidden">
        {getStatusModal()}
        {showDeleteModal && getDeleteAccountModal()}
        {showErrorMessage && (
          <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
            {showErrorMessage}
          </ErrorModal>
        )}

        <p className="text-2xl text-dark dark:text-white">{t("settings.review_info")}</p>
        {user && (
          <p className="text-dark dark:text-white">
            <label className="text-dark dark:text-white font-bold text-lg">
              {formTranslations.email}:
            </label>{" "}
            {user.email}
          </p>
        )}
        <br />
        <hr className="new1 " />
        <br />
        <form key={1} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
          <label className="text-docket-primary font-extrabold text-2xl">
            {t("settings.update_name")}
          </label>
          <div className="flex gap-1 max-md:flex-col">
            <div className="md:flex-1/2">
              {getInputField("firstName", formTranslations.firstName, user?.first_name || "")}
            </div>
            <div className="md:flex-1/2">
              {getInputField("lastName", formTranslations.lastName, user?.last_name || "")}
            </div>
          </div>
          <button
            className="btn docket-button-responsive mt-1 px-4 w-fit"
            type="submit"
            disabled={!isValid}
          >
            {t("settings.update_name")}
          </button>
        </form>
        <br />
        <hr className="new1" />
        <br />
        <form key={3}>
          <label className="text-docket-primary font-extrabold text-2xl">
            {t("settings.update_profile")}
          </label>
          {getUserUpdateForm()}
          <button
            className="btn docket-button-responsive mt-2 px-4 w-fit"
            type="button" // Change the type to "button" to prevent default form submission
            onClick={() => onSubmitUserInfo()} // Manually invoke the form submission
          >
            {t("settings.update_profile")}
          </button>
        </form>
        <br />
        <hr className="new1" />
        <br />
        <div className="flex flex-col gap-1">
          <label className="text-docket-primary font-extrabold text-2xl">Update Appearance</label>
          <div className="text-dark dark:text-white flex gap-1">
            <DocketSwitch
              checked={highContrast}
              onChange={(e) => {
                const checked = e.target.checked;
                setHighContrast(checked);
              }}
              id={"high-contrast-mode"}
              name={"high-contrast-mode"}
            />
            <span className="ml-4 text-dark dark:text-white mobile-text-color">
              High-contrast Mode
            </span>
          </div>
        </div>
        <br />
        <hr className="new1" />
        <br />
        <label className="text-docket-primary font-extrabold text-2xl">
          {t("settings.update_contact_phone_number")}
        </label>
        {user?.verified_phone_number && (
          <p className="text-md font-extrabold text-black dark:text-white mobile-text-color">
            {t("settings.current_phone_number", {
              phoneNumber: formatNumber(user?.verified_phone_number ?? ""),
            })}
          </p>
        )}
        <div className="mt-1 ml-1">
          {verifyStep && <VerifyPhone afterSubmit={() => setVerifyStep(false)} />}
          <br />
          {!verifyStep && <EnterPhonePin />}
        </div>
        <br />
        <hr className="new1" />
        <br />
        <div className="flex flex-col gap-1">
          <label className="text-docket-primary font-extrabold text-2xl">
            {t("settings.notif_prefs_header")}
          </label>
          <div className="text-dark dark:text-white flex gap-1">
            <DocketSwitch
              checked={selectedDisableAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedDisableAll(checked);
                if (checked === true) {
                  setSelectedEmailRecordUpdates(false);
                  setSelectedEmailReminders(false);
                  setSelectedAppUpdates(false);
                }
              }}
              id={"disable-notif"}
              name={"disable-notif"}
            />
            <span className="ml-4 mobile-text-color">{t("settings.notif_prefs_disable")}</span>
          </div>
          <p className="text-xs text-black dark:text-white italic mobile-text-color">
            {t("settings.notif_disable_message")}
          </p>
          <label className="text-black dark:text-white mobile-text-color font-bold mobile-text-color">
            {t("settings.notif_prefs_email")}
          </label>
          <div className="text-dark dark:text-white flex gap-1">
            <DocketSwitch
              disabled={selectedDisableAll}
              checked={selectedEmailRecordUpdates}
              onChange={(e) => setSelectedEmailRecordUpdates(e.target.checked)}
              id={"results-notif"}
              name={"results-notif"}
            />
            <span className={`ml-4 mobile-text-color ${selectedDisableAll ? "has-text-grey" : ""}`}>
              {t("settings.notif_immunization_records_updates")}
            </span>
          </div>
          <div className="text-dark dark:text-white flex gap-1">
            <DocketSwitch
              disabled={selectedDisableAll}
              checked={selectedEmailReminders}
              onChange={(e) => setSelectedEmailReminders(e.target.checked)}
              id={"vaccine-notif"}
              name={"vaccine-notif"}
            />
            <span className={`ml-4 mobile-text-color ${selectedDisableAll ? "has-text-grey" : ""}`}>
              {t("settings.notif_immunization_reminders")}
            </span>
          </div>
          <div className="text-dark dark:text-white flex gap-1">
            <DocketSwitch
              disabled={selectedDisableAll}
              checked={selectedAppUpdates}
              onChange={(e) => setSelectedAppUpdates(e.target.checked)}
              id={"updates-notif"}
              name={"updates-notif"}
            />
            <span className={`ml-4 mobile-text-color ${selectedDisableAll ? "text-grey" : ""}`}>
              {t("settings.notif_immunization_versions")}
            </span>
          </div>
          <button
            className="btn docket-button-responsive pr-4 pl-4 w-fit"
            onClick={() => handleSaveNotificationPreferences()}
            disabled={!notificationHasChanged()}
          >
            {t("settings.update_notifications")}
          </button>
        </div>
        <br />
        <hr className="new1" />
        <br />
        <form
          key={4}
          onSubmit={handleDeleteAccount(() => setShowDeleteModal(true))}
          className="flex flex-col gap-2"
        >
          <label className="text-docket-primary font-extrabold text-2xl">
            {t("settings.delete_account")}
          </label>
          <p className="font-extrabold text-black dark:text-white mobile-text-color">
            {t("settings.delete_account_confirmation_prompt")}
          </p>
          {getDeleteAccountForm()}
          <button
            className="btn docket-button-responsive pr-4 pl-4 w-fit"
            type="submit"
            disabled={!deleteAccountChecked}
          >
            {t("settings.delete_button_label")}
          </button>
        </form>
      </div>
    </>
  );
}

export default Settings;
